import {
  type Category,
  type CategorySubtype,
  type EntityCategory,
} from "@digits-graphql/frontend/graphql-bearer"
import { CATEGORY_NUMBER_REGEX } from "@digits-shared/components/UI/Elements/Form/Form"
import { molecule } from "bunshi"
import { use } from "bunshi/react"
import { atom } from "jotai"
import { NewCategoryModalScope } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/NewCategoryModalScope"
import { CATEGORY_NUMBER_PREFIX_MAP } from "src/frontend/components/OS/Applications/CategoryManager/Shared"
import {
  isCategory,
  isNewEntityId,
  type NewCategoryEntity,
} from "src/shared/components/Typeahead/sharedTypeahead"

export const newCategoryModalMolecule = molecule(() => {
  use(NewCategoryModalScope)

  /* State */
  const initialCategoryAtom = atom<Category | NewCategoryEntity | undefined>(undefined)
  const existingCategoryNumbersAtom = atom<Set<string>>(new Set<string>())
  const existingCategoryNamesAtom = atom<Set<string>>(new Set<string>())
  const parentCategoryAtom = atom<EntityCategory | undefined>()
  const categoryNameAtom = atom<string>("")
  const subtypeAtom = atom<CategorySubtype | undefined>()
  const iconDisplayKeyAtom = atom<string | undefined>()
  const categoryNumberAtom = atom<string>("")
  const descriptionAtom = atom<string>("")
  const originalCategoryNumberAtom = atom<string | undefined>(undefined)

  /* Derived State */
  const completeCategoryNumberAtom = atom<string>((get) => {
    const parentCategory = get(parentCategoryAtom)
    const categoryNumber = get(categoryNumberAtom)

    if (parentCategory && categoryNumber) {
      return `${CATEGORY_NUMBER_PREFIX_MAP.get(parentCategory.type)}${categoryNumber}`
    }

    return ""
  })

  const excludedParentCategoryIdsAtom = atom((get) => {
    const initialCategory = get(initialCategoryAtom)
    return initialCategory && !isNewEntityId(initialCategory.id) ? [initialCategory.id] : []
  })

  const normalizedCategoryNamesAtom = atom(
    (get) =>
      new Set(
        Array.from(get(existingCategoryNamesAtom).entries()).map(([name]) =>
          name.trim().toLowerCase()
        )
      )
  )

  const isCategoryNameUniqueAtom = atom((get) => {
    const categoryName = get(categoryNameAtom)

    return categoryName
      ? !get(normalizedCategoryNamesAtom).has(categoryName.trim().toLowerCase())
      : true
  })

  const isValidAtom = atom((get) => {
    const categoryNumberRegExp = new RegExp(CATEGORY_NUMBER_REGEX)

    const initialCategory = get(initialCategoryAtom)
    const originalCategoryNumber =
      isCategory(initialCategory) && get(shouldUpdateCategoryAtom)
        ? initialCategory.displayNumber
        : undefined

    const categoryNumber = get(completeCategoryNumberAtom)

    const isCategoryNumberUnique =
      categoryNumber && categoryNumber !== originalCategoryNumber
        ? !get(existingCategoryNumbersAtom).has(categoryNumber)
        : true

    return Boolean(
      get(parentCategoryAtom) &&
        get(subtypeAtom) &&
        get(categoryNameAtom) &&
        get(iconDisplayKeyAtom) &&
        isCategoryNumberUnique &&
        categoryNumberRegExp.test(categoryNumber)
    )
  })

  /* Actions */
  const initializeModalStateAtom = atom(null, (_, set, category?: Category | NewCategoryEntity) => {
    const isCategoryType = isCategory(category)
    const categoryNumber = isCategoryType ? category.displayNumber || undefined : undefined
    const parentCategory = isCategoryType
      ? category.ancestors?.find((a) => a.id === category.parentCategoryId)
      : undefined

    set(initialCategoryAtom, category)
    set(parentCategoryAtom, parentCategory)
    set(categoryNameAtom, category?.name || "")
    set(iconDisplayKeyAtom, category?.displayKey || undefined)
    set(subtypeAtom, isCategoryType ? category.subtype : undefined)
    set(categoryNumberAtom, categoryNumber?.substring(1) || "")
    set(originalCategoryNumberAtom, categoryNumber)
    set(descriptionAtom, isCategoryType ? category.description : "")
  })

  // We want to update instead of create if our initial category was a Category type
  // and the subtype and parent category didn't change (otherwise we create a new category)
  const shouldUpdateCategoryAtom = atom((get) => {
    const initialCategory = get(initialCategoryAtom)
    const isCategoryType = isCategory(initialCategory)

    if (!isCategoryType || !initialCategory) return false

    return (
      initialCategory.subtype === get(subtypeAtom) &&
      initialCategory.parentCategoryId === get(parentCategoryAtom)?.id
    )
  })

  return {
    existingCategoryNumbersAtom,
    existingCategoryNamesAtom,
    initialCategoryAtom,
    originalCategoryNumberAtom,
    parentCategoryAtom,
    categoryNameAtom,
    subtypeAtom,
    categoryNumberAtom,
    iconDisplayKeyAtom,
    completeCategoryNumberAtom,
    descriptionAtom,
    isValidAtom,
    isCategoryNameUniqueAtom,
    initializeModalStateAtom,
    shouldUpdateCategoryAtom,
    excludedParentCategoryIdsAtom,
  }
})
